import { useEffect, useState } from 'react';
import type { PermissionsMap, PublicUser, Scope } from '@otter/shared';
import { api } from '../../lib/api';
import { fmtDate, todayIso } from '../../lib/format';

type Props = {
  actor: PublicUser;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
};

type RoleOption = {
  id: string;
  slug: string;
  name: string;
  scope: Scope;
  isSystem: boolean;
  permissions: PermissionsMap;
};

type Row = {
  id: string;
  code: string;
  name: string;
  email: string;
  color: 'A' | 'B' | 'C' | 'D';
  roleId: string;
  roleSlug: string;
  roleName: string;
  roleScope: Scope;
  managerId: string | null;
  isSystemAdmin: boolean;
  active: boolean;
  joinedAt: string;
  clientsCount: number;
  invoicesCount: number;
};

export function Comerciales({ actor, onToast, onError }: Props) {
  const [items, setItems] = useState<Row[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<Row | null>(null);

  async function load() {
    try {
      const r = await api<{ items: Row[] }>('/admin/users');
      setItems(r.items);
      const rs = await api<{ items: RoleOption[] }>('/admin/roles');
      setRoles(rs.items);
    } catch (e) {
      onError(e);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="adm-section">
      <div className="stitle">Equipo</div>
      <h2 style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        Usuarios
        <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={() => setShowCreate(true)}>
          <span className="plus">+</span> Alta usuario
        </button>
      </h2>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table className="t">
          <thead>
            <tr>
              <th>Code</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Manager</th>
              <th>Clientes</th>
              <th>Facturas</th>
              <th>Alta</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td>
                  <span className={`av-mini av-${u.color}`}>{u.code}</span>
                </td>
                <td>
                  <b>{u.name}</b>
                  {u.isSystemAdmin && (
                    <span className="pill" style={{ marginLeft: 6 }}>SYSTEM</span>
                  )}
                </td>
                <td className="mono">{u.email}</td>
                <td>{u.roleName}</td>
                <td>
                  {u.managerId
                    ? items.find((x) => x.id === u.managerId)?.code ?? '—'
                    : '—'}
                </td>
                <td>{u.clientsCount}</td>
                <td>{u.invoicesCount}</td>
                <td className="mono" style={{ whiteSpace: 'nowrap' }}>{fmtDate(u.joinedAt)}</td>
                <td>
                  <span className={`pill ${u.active ? 'ok' : 'draft'}`}>
                    {u.active ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </td>
                <td>
                  <div className="row-actions">
                    <button
                      className="btn sm"
                      onClick={() => setEditingUser(u)}
                      disabled={u.isSystemAdmin && !actor.isSystemAdmin}
                      title={u.isSystemAdmin && !actor.isSystemAdmin ? 'Solo el system admin puede editar' : 'Editar usuario'}
                    >
                      editar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showCreate && (
        <CreateUserModal
          roles={roles.filter((r) => actor.isSystemAdmin || r.slug !== 'admin')}
          users={items}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
            onToast('Usuario dado de alta', 'ok');
          }}
          onError={onError}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles.filter((r) => actor.isSystemAdmin || r.slug !== 'admin')}
          users={items.filter((x) => x.id !== editingUser.id)}
          actorIsSystemAdmin={actor.isSystemAdmin}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            setEditingUser(null);
            onToast('Usuario actualizado', 'ok');
            void load();
          }}
          onToast={onToast}
          onError={onError}
        />
      )}
    </div>
  );
}

function EditUserModal(props: {
  user: Row;
  roles: RoleOption[];
  users: Row[];
  actorIsSystemAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
  onToast: (msg: string, kind?: 'ok' | 'err') => void;
  onError: (e: unknown) => void;
}) {
  const [name, setName] = useState(props.user.name);
  const [email, setEmail] = useState(props.user.email);
  const [roleId, setRoleId] = useState(props.user.roleId);
  const [managerId, setManagerId] = useState<string | null>(props.user.managerId);
  const [color, setColor] = useState<'A' | 'B' | 'C' | 'D'>(props.user.color);
  const [busy, setBusy] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);

  const lockRole = props.user.isSystemAdmin || !props.actorIsSystemAdmin;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body: Record<string, unknown> = { name, email, color };
      if (!lockRole) body.roleId = roleId;
      if (!props.user.isSystemAdmin) body.managerId = managerId;
      await api(`/admin/users/${props.user.id}`, { method: 'PATCH', body });
      props.onSaved();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive() {
    setBusy(true);
    try {
      await api(`/admin/users/${props.user.id}`, {
        method: 'PATCH',
        body: { active: !props.user.active },
      });
      props.onToast(props.user.active ? 'Usuario desactivado' : 'Usuario activado', 'ok');
      props.onSaved();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Editar usuario · {props.user.code}</h3>
        <div className="field"><label>Nombre</label><input value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div className="field">
          <label>Rol</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={lockRole}>
            {props.roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.scope}
              </option>
            ))}
          </select>
        </div>
        {!props.user.isSystemAdmin && (
          <div className="field">
            <label>Manager (opcional · sólo aplica si su rol tiene scope &quot;team&quot;)</label>
            <select
              value={managerId ?? ''}
              onChange={(e) => setManagerId(e.target.value === '' ? null : e.target.value)}
            >
              <option value="">(sin manager)</option>
              {props.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.code} · {u.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label>Color avatar</label>
          <select value={color} onChange={(e) => setColor(e.target.value as 'A')}>
            <option value="A">A · acento</option>
            <option value="B">B · azul</option>
            <option value="C">C · naranja</option>
            <option value="D">D · morado</option>
          </select>
        </div>
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: '1px solid var(--line)',
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            className="btn sm"
            onClick={() => setShowResetPwd(true)}
            disabled={busy}
          >
            resetear contraseña
          </button>
          {!props.user.isSystemAdmin && (
            <button type="button" className="btn sm" onClick={toggleActive} disabled={busy}>
              {props.user.active ? 'desactivar usuario' : 'activar usuario'}
            </button>
          )}
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={busy}>Guardar</button>
        </div>
      </form>
      {showResetPwd && (
        <ResetPasswordModal
          userId={props.user.id}
          onClose={() => setShowResetPwd(false)}
          onDone={() => {
            setShowResetPwd(false);
            props.onToast('Contraseña reseteada', 'ok');
          }}
          onError={props.onError}
        />
      )}
    </div>
  );
}

function CreateUserModal(props: {
  roles: RoleOption[];
  users: Row[];
  onClose: () => void;
  onCreated: () => void;
  onError: (e: unknown) => void;
}) {
  const defaultRole =
    props.roles.find((r) => r.slug === 'comercial')?.id ?? props.roles[0]?.id ?? '';
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [color, setColor] = useState<'A' | 'B' | 'C' | 'D'>('A');
  const [roleId, setRoleId] = useState(defaultRole);
  const [managerId, setManagerId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/admin/users', {
        method: 'POST',
        body: {
          code: code.toUpperCase(),
          name,
          email,
          password,
          color,
          roleId,
          managerId,
          joinedAt: todayIso(),
        },
      });
      props.onCreated();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Alta usuario</h3>
        <div className="field"><label>Code (2-3 letras)</label><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={3} required /></div>
        <div className="field"><label>Nombre</label><input value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div className="field"><label>Password inicial</label><input value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
        <div className="field">
          <label>Rol</label>
          <select value={roleId} onChange={(e) => setRoleId(e.target.value)} required>
            {props.roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.scope}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Manager (opcional)</label>
          <select
            value={managerId ?? ''}
            onChange={(e) => setManagerId(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">(sin manager)</option>
            {props.users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code} · {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Color avatar</label>
          <select value={color} onChange={(e) => setColor(e.target.value as 'A')}>
            <option value="A">A · acento</option>
            <option value="B">B · azul</option>
            <option value="C">C · naranja</option>
            <option value="D">D · morado</option>
          </select>
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={busy}>Crear</button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal(props: {
  userId: string;
  onClose: () => void;
  onDone: () => void;
  onError: (e: unknown) => void;
}) {
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api(`/admin/users/${props.userId}/reset-password`, {
        method: 'POST',
        body: { newPassword: pwd },
      });
      props.onDone();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Reset password</h3>
        <div className="field">
          <label>Nueva password (≥8)</label>
          <input value={pwd} onChange={(e) => setPwd(e.target.value)} minLength={8} required />
        </div>
        <div className="actions">
          <button type="button" className="btn ghost" onClick={props.onClose}>Cancelar</button>
          <button type="submit" className="btn primary" disabled={busy}>Aplicar</button>
        </div>
      </form>
    </div>
  );
}
